import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  api,
  cashoutTicket,
  endpoints,
  fetchCashoutList,
  getAccount,
  getUserSettings,
  queryKeys,
  updateUserCredits,
  useAuthSession,
  type BetSettings,
  type CashoutSettings,
  type CashoutTicketResponse,
  type CreditsData,
} from '@/api';
import {
  MIN_CASHOUT_OFFER,
  buildOpenCashoutTicket,
  canShowPartialCashout,
  isCashoutEligible,
  type CashoutHistoryEntry,
} from '@/domain/cashout';
import type { TicketListItem } from '@/domain/tickets';
import { useOptionalLiveFeed } from '@/live/LiveFeedProvider';
import { useLiveFeedSocket } from '@/live/useLiveFeedSocket';
import {
  applyCashoutListResponse,
  applyCashoutOddsPatch,
  applySuccessfulFullCashout,
  applySuccessfulPartialCashout,
  clearCashoutHistory,
  clearCashoutStore,
  clearCashoutUpdating,
  getCashoutHistory,
  getCashoutOffer,
  getCashoutSnapshot,
  getOpenCashoutTickets,
  hasOpenCashoutTickets,
  setCashoutOfferAmount,
  setCashoutSettings,
  setOpenCashoutTickets,
  subscribeCashout,
} from './cashout-store';

export type CashoutControlUiState =
  | 'hidden'
  | 'available'
  | 'pending'
  | 'confirm'
  | 'unavailable';

export interface UseCashoutResult {
  version: number;
  allowPartial: boolean;
  cashoutEnabled: boolean;
  /** True while cashoutlist is in flight (show Pending instead of Unavailable). */
  isListFetching: boolean;
  getOffer: (ticketId: string) => number;
  isUpdating: (ticketId: string) => boolean;
  getHistory: (ticketId: string) => CashoutHistoryEntry[];
  isTicketEligible: (ticket: TicketListItem) => boolean;
  executeCashout: (args: {
    ticketId: string;
    cashoutValue: number;
    partialPercent?: number;
  }) => Promise<CashoutTicketResponse>;
  isExecuting: boolean;
  executingTicketId: string | null;
  errorMessage: string | null;
  infoMessage: string | null;
  clearMessages: () => void;
  acknowledgeUpdating: (ticketId: string) => void;
}

async function refreshCredits(machineGuid: string): Promise<void> {
  try {
    const { host, path } = endpoints.player.refreshCredits;
    const credits = await api.post<CreditsData>(host, path, { Value: machineGuid });
    if (credits && typeof credits === 'object') {
      updateUserCredits(credits);
    }
  } catch {
    // Ticket list invalidation still refreshes the UI without balance.
  }
}

/**
 * Keeps cashout offers in sync for the current My Bets ticket list:
 * cashoutlist seed + live odds patches + execute mutation.
 */
export function useCashout(
  tickets: TicketListItem[],
  enabled: boolean,
): UseCashoutResult {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const liveFeed = useOptionalLiveFeed();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const executingTicketIdRef = useRef<string | null>(null);

  const version = useSyncExternalStore(
    subscribeCashout,
    getCashoutSnapshot,
    getCashoutSnapshot,
  );

  // Re-read settings only when the auth session version bumps (login/logout/credits).
  // getUserSettings() JSON.parses every call, so object identity is never stable.
  const { version: authVersion } = useAuthSession();
  const cashoutSettings = useMemo<CashoutSettings | null>(() => {
    return getUserSettings()?.CashoutSettings ?? null;
  }, [authVersion]);
  const betSettings = useMemo<BetSettings | undefined>(() => {
    return getUserSettings()?.BetSettings ?? getAccount()?.BetSettings;
  }, [authVersion]);
  const machineGuid = useMemo(
    () => getUserSettings()?.Guid?.trim() ?? '',
    [authVersion],
  );

  const cashoutEnabled = Boolean(
    enabled && cashoutSettings?.AllowCashout && betSettings?.AllowCashout,
  );
  const allowPartial = canShowPartialCashout(cashoutSettings, betSettings);

  const isLiveRoute = useRouterState({
    select: (state) => {
      const pathname = state.location.pathname;
      return pathname.startsWith('/live') && !pathname.startsWith('/livecasino');
    },
  });

  const eligibleIdsKey = useMemo(() => {
    if (!cashoutEnabled) return '';
    return tickets
      .filter((ticket) => isCashoutEligible(ticket, cashoutSettings, betSettings))
      .map((ticket) => ticket.Id)
      .join('|');
  }, [betSettings, cashoutEnabled, cashoutSettings, tickets]);

  useEffect(() => {
    setCashoutSettings(cashoutSettings);
  }, [cashoutSettings]);

  useEffect(() => {
    if (!cashoutEnabled) {
      clearCashoutStore();
      return;
    }
    const eligible = tickets.filter((ticket) =>
      isCashoutEligible(ticket, cashoutSettings, betSettings),
    );
    setOpenCashoutTickets(eligible.map(buildOpenCashoutTicket));
  }, [betSettings, cashoutEnabled, cashoutSettings, eligibleIdsKey, tickets]);

  const listQuery = useQuery({
    queryKey: [...queryKeys.tickets.cashoutList(), eligibleIdsKey],
    enabled: cashoutEnabled && eligibleIdsKey.length > 0,
    staleTime: 15_000,
    // Avoid a stuck empty cache from the pre-store-seed race on remount.
    refetchOnMount: 'always',
    queryFn: async ({ signal }) => {
      // Build from the ticket list here — do not read the store. React Query can
      // run before the setOpenCashoutTickets effect, which used to cache [].
      const open = tickets
        .filter((ticket) => isCashoutEligible(ticket, cashoutSettings, betSettings))
        .map(buildOpenCashoutTicket);
      if (open.length === 0) return [];
      setOpenCashoutTickets(open);
      return fetchCashoutList(open, signal);
    },
  });

  useEffect(() => {
    if (!listQuery.isSuccess || !listQuery.data) return;
    // Empty array is a valid "nothing priced" response; still apply so offers clear.
    applyCashoutListResponse(listQuery.data);
  }, [listQuery.data, listQuery.isSuccess, listQuery.dataUpdatedAt]);

  const hasTickets = hasOpenCashoutTickets();
  useLiveFeedSocket(cashoutEnabled && hasTickets && !isLiveRoute);

  useEffect(() => {
    if (!liveFeed || !cashoutEnabled) return;
    const applyPatches = (onlyEventIds?: ReadonlySet<number>) => {
      const eventIds = new Set<number>();
      for (const ticket of getOpenCashoutTickets()) {
        for (const item of ticket.Items) {
          if (!onlyEventIds || onlyEventIds.has(item.Event.EventId)) {
            eventIds.add(item.Event.EventId);
          }
        }
      }
      for (const eventId of eventIds) {
        const markets = liveFeed.getOddsPatch(eventId);
        const meta = liveFeed.getEventMetaPatch(eventId);
        if (markets || meta) {
          applyCashoutOddsPatch(eventId, markets, meta?.active);
        }
      }
    };
    applyPatches();
    return liveFeed.subscribe(applyPatches);
  }, [liveFeed, cashoutEnabled, eligibleIdsKey]);

  const mutation = useMutation({
    mutationFn: async (args: {
      ticketId: string;
      cashoutValue: number;
      partialPercent?: number;
    }) => {
      if (!machineGuid) {
        throw new Error(t('CashoutNotAvailable', { defaultValue: 'Cashout not available' }));
      }
      executingTicketIdRef.current = args.ticketId;
      const open = getOpenCashoutTickets().find((ticket) => ticket.Id === args.ticketId);
      const body: Parameters<typeof cashoutTicket>[0] = {
        Id: args.ticketId,
        User: machineGuid,
        CashoutValue: args.cashoutValue,
        Options: betSettings?.Options,
      };
      if (args.partialPercent != null) {
        body.PartialCashoutPercentage = args.partialPercent;
        const initial = open?.InitialStake ?? open?.Stake ?? 0;
        const stakeUsed =
          Math.floor(((open?.Stake ?? 0) * (args.partialPercent / 100) + Number.EPSILON) * 100) /
          100;
        body.TotalCashoutStake = initial - (open?.Stake ?? 0) + stakeUsed;
      }
      return cashoutTicket(body);
    },
    onSuccess: async (response, variables) => {
      if (response.Changed && response.ChangedAmount != null) {
        setCashoutOfferAmount(variables.ticketId, response.ChangedAmount);
        setInfoMessage(
          t('CashoutValueChanged', { defaultValue: 'Cashout value has changed' }),
        );
        setErrorMessage(null);
        return;
      }
      if (!response.Success) {
        const key = response.Error || 'CashoutNotAvailable';
        setErrorMessage(t(key, { defaultValue: key }));
        setInfoMessage(null);
        return;
      }
      if (variables.partialPercent != null) {
        applySuccessfulPartialCashout(
          variables.ticketId,
          variables.partialPercent,
          response.Amount ?? variables.cashoutValue,
        );
      } else {
        applySuccessfulFullCashout(variables.ticketId);
      }
      setErrorMessage(null);
      setInfoMessage(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      // Ticket list now owns PartialCashoutHistory — drop optimistic session rows.
      if (variables.partialPercent != null) {
        clearCashoutHistory(variables.ticketId);
      }
      if (machineGuid) await refreshCredits(machineGuid);
    },
    onError: (error: Error) => {
      setErrorMessage(
        error.message ||
          t('CashoutNotAvailable', { defaultValue: 'Cashout not available' }),
      );
      setInfoMessage(null);
    },
    onSettled: () => {
      executingTicketIdRef.current = null;
    },
  });

  return {
    version,
    allowPartial,
    cashoutEnabled,
    isListFetching: listQuery.isFetching,
    getOffer: (ticketId) => getCashoutOffer(ticketId)?.offer ?? 0,
    isUpdating: (ticketId) => Boolean(getCashoutOffer(ticketId)?.updating),
    getHistory: (ticketId) => getCashoutHistory(ticketId),
    isTicketEligible: (ticket) =>
      isCashoutEligible(ticket, cashoutSettings, betSettings),
    executeCashout: (args) => mutation.mutateAsync(args),
    isExecuting: mutation.isPending,
    executingTicketId: mutation.isPending ? executingTicketIdRef.current : null,
    errorMessage,
    infoMessage,
    clearMessages: () => {
      setErrorMessage(null);
      setInfoMessage(null);
    },
    acknowledgeUpdating: clearCashoutUpdating,
  };
}

export { MIN_CASHOUT_OFFER };
